import type { CascaderOption, CascaderOptionWithPath } from '../types';
import { isSubmenu } from '../util';
import { subtreeContainsSuggestionLeaf } from './subtreeContainsSuggestionLeaf';

const pathsEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((segment, i) => segment === b[i]);

const isSuggestionLeaf = (o: CascaderOption): boolean =>
  Boolean((o as { isSuggestion?: boolean }).isSuggestion);

function pathHasSuggestedBranchAncestor<OptionType extends CascaderOption>(
  rootMenuOptions: OptionType[],
  fullPath: string[],
): boolean {
  let levelOptions = rootMenuOptions;
  for (let depth = 0; depth < fullPath.length - 1; depth++) {
    const seg = fullPath[depth];
    const node = levelOptions.find((o) => o.value === seg);
    if (!node || !isSubmenu(node)) return false;
    if ((node as { isSuggestedBranch?: boolean }).isSuggestedBranch) {
      return true;
    }
    levelOptions = node.children.flatMap((g) => g.children);
  }
  return false;
}

export function computeCascaderSuggestionFlags<
  OptionType extends CascaderOption,
>(
  isSuggestionsEnabled: boolean,
  flatOptions: CascaderOptionWithPath<OptionType>[],
  selectedOption: CascaderOptionWithPath<OptionType> | undefined,
  rootMenuOptions?: OptionType[],
): {
  hasSuggestions: boolean;
  hasAcceptedSuggestions: boolean;
  hasRejectedSuggestions: boolean;
} {
  let hasAcceptedSuggestions = false;
  let hasRejectedSuggestions = false;

  if (!isSuggestionsEnabled) {
    return {
      hasSuggestions: false,
      hasAcceptedSuggestions: false,
      hasRejectedSuggestions: false,
    };
  }

  const suggestionLeaves = flatOptions.filter((o) => isSuggestionLeaf(o));
  const treeSignals =
    rootMenuOptions !== undefined &&
    rootMenuOptions.some((o) => subtreeContainsSuggestionLeaf(o));

  const hasSuggestions = suggestionLeaves.length > 0 || treeSignals;

  if (!hasSuggestions) {
    return {
      hasSuggestions: false,
      hasAcceptedSuggestions: false,
      hasRejectedSuggestions: false,
    };
  }

  if (!selectedOption) {
    return {
      hasSuggestions: true,
      hasAcceptedSuggestions: false,
      hasRejectedSuggestions: false,
    };
  }

  if (suggestionLeaves.length > 0) {
    for (const leaf of suggestionLeaves) {
      if (pathsEqual(selectedOption.fullPath, leaf.fullPath)) {
        hasAcceptedSuggestions = true;
      } else {
        hasRejectedSuggestions = true;
      }
    }
  }

  const rootOpts = rootMenuOptions ?? [];
  if (
    !hasAcceptedSuggestions &&
    rootOpts.length > 0 &&
    pathHasSuggestedBranchAncestor(rootOpts, selectedOption.fullPath)
  ) {
    hasAcceptedSuggestions = true;
    hasRejectedSuggestions = false;
  } else if (!hasAcceptedSuggestions) {
    hasRejectedSuggestions = true;
  }

  return {
    hasSuggestions,
    hasAcceptedSuggestions,
    hasRejectedSuggestions,
  };
}
